import { FormProvider } from '@neo/blocks/forms/form-provider'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import { Button } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import type React from 'react'
import { useMemo } from 'react'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import type { IPAccessControlList } from 'v2/types/api/settings'
import { generateToastsForMutation } from 'v2/utils/toasts'
import { useCreateIPACLMutation, useDeleteIPACLMutation, useUpdateIPACLMutation } from '../queries'
import {
  type IPAccessControlListFormValues,
  ipAccessControlListFormFields,
} from './ip-acl-modal.config'

interface IPACLModalProps {
  isOpen: boolean
  onClose: () => void
  editData?: IPAccessControlList | null
}

export const IPACLModal: React.FC<IPACLModalProps> = ({ isOpen, onClose, editData }) => {
  const isEditing = !!editData

  const createMutation = useCreateIPACLMutation()
  const updateMutation = useUpdateIPACLMutation()
  const deleteMutation = useDeleteIPACLMutation()

  const handleSubmit = (values: IPAccessControlListFormValues) => {
    if (isEditing && editData) {
      updateMutation.mutate(
        {
          ipacl_uuid: editData.ipacl_uuid,
          ...values,
        },
        generateToastsForMutation(
          {
            title: 'Success',
            description: 'IP Access Control List updated successfully',
            callback: onClose,
          },
          {
            title: 'Error',
            description: 'Failed to update IP Access Control List',
          },
        ),
      )
    } else {
      createMutation.mutate(
        values,
        generateToastsForMutation(
          {
            title: 'Success',
            description: 'IP Access Control List created successfully',
            callback: onClose,
          },
          {
            title: 'Error',
            description: 'Failed to create IP Access Control List',
          },
        ),
      )
    }
  }

  const handleDelete = () => {
    if (!editData) return
    deleteMutation.mutate(
      editData.ipacl_uuid,
      generateToastsForMutation(
        {
          title: 'Success',
          description: 'IP Access Control List deleted successfully',
          callback: onClose,
        },
        {
          title: 'Error',
          description: 'Failed to delete IP Access Control List',
        },
      ),
    )
  }

  const defaultValues: IPAccessControlListFormValues = useMemo(
    () => ({
      name: editData?.name || '',
      ip_addresses: editData?.ip_addresses || [],
    }),
    [editData],
  )

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit IP access control list' : 'Create new IP access control list'}
          </DialogTitle>
        </DialogHeader>

        <FormProvider>
          <GenericForm<IPAccessControlListFormValues>
            fields={ipAccessControlListFormFields}
            onSubmit={handleSubmit}
            defaultValues={defaultValues}
            className="grid grid-cols-2 gap-4"
            id="ip-acl-form"
            includeSubmitButton={false}
          />
          <DialogFooter className="sm:flex-row sm:justify-between sm:space-x-2">
            {isEditing && (
              <Button
                type="button"
                variant="destructive-outline"
                onClick={handleDelete}
                data-testid="button-delete-ip-acl"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            )}
            <FormSubmitButton
              formId="ip-acl-form"
              loading={createMutation.isPending || updateMutation.isPending}
              data-testid="button-save-ip-acl"
            >
              {isEditing ? 'Update ACL' : 'Create ACL'}
            </FormSubmitButton>
          </DialogFooter>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
